import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo } from "react";
import { useSimulationDistanceFromTrend } from "./useSimulationDistanceFromTrend";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { useChartActiveElement } from "@app/components/ChartTooltip";
import { ChartDftTotalReturnsSummary } from "@app/pages/assetAllocation/editPortfolioGroup/ChartDftTotalReturnsSummary";
import { ChartDftTotalReturns } from "@app/components/ChartDftTotalReturns";
import { ChartDftVersusTenYearReturn } from "@app/components/ChartDftVersusTenYearReturn";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import { CarDownloadButton } from "@app/components/Buttons";
import { CarSelectOptionField } from "@app/components/Inputs";
import {
  DftChartKind,
  useDftChartKindSelector,
} from "../assetClassAnalysis/useDftChartKindSelector";
import { ChartDftVersusTenYearReturnBoxAndWhisker } from "@app/components/ChartDftVersusTenYearReturnBoxAndWhisker";

interface SimulationDistanceFromTrendProps {
  simulationJobId: string;
}

export const SimulationDistanceFromTrend = memo(
  (props: SimulationDistanceFromTrendProps) => {
    const data = useSimulationDistanceFromTrend({
      simulationJobId: props.simulationJobId,
    });

    const pageContent = usePageContent();

    const activeElement = useChartActiveElement();

    const dftChartKindSelector = useDftChartKindSelector({
      storageKey: "SimulationDistanceFromTrend_dftChartKindSelector",
    });

    return (
      <TablePlaceHolder
        sx={{ mx: 7, mt: 5 }}
        isLoading={data.assetClasses.isLoading || pageContent.isLoading}
        rows={8}
      >
        <CarPageContent
          sx={{ mb: 3 }}
          content={pageContent.getContent(
            CarPageContentCode.SIMULATION_ANALYSIS_PRICE_DRIVEN_DISTANCE_FROM_TREND,
          )}
          isWhiteContext
        />
        <Box sx={{ mt: 1, display: "flex", gap: 5, alignItems: "flex-end" }}>
          <CarAssetClassSelector
            label="Asset Class"
            sx={{
              width: 250,
              ".MuiInputLabel-root": {
                fontSize: "16px",
              },
              ".MuiOutlinedInput-root": {
                marginTop: "28px",
                bgcolor: "white",
              },
            }}
            {...data.assetClassSelector}
          />
          <CarDownloadButton
            sx={{ ml: "auto", mb: 0.5 }}
            onClick={data.handleDownloadReports}
          >
            Download Reports
          </CarDownloadButton>
        </Box>
        <TablePlaceHolder sx={{ mt: 5 }} isLoading={data.isLoading} rows={5}>
          {data.dftItem && (
            <>
              <ChartDftTotalReturnsSummary
                sx={{ mt: 5 }}
                dftItem={data.dftItem}
                showFormula
                isWhiteContext={false}
              />

              <ChartDftTotalReturns
                sx={{ mt: 6.5 }}
                dftItem={data.dftItem}
                pendoPrefix="asset_class_analysis_dft_total_returns"
                activeElement={activeElement}
                isWhiteContext={false}
              />
              <Box
                sx={{ mt: 5, display: "flex", gap: 2, alignItems: "center" }}
              >
                <Typography variant="h6SemiBold">Chart Selection -</Typography>
                <CarSelectOptionField
                  sx={{ width: 180 }}
                  {...dftChartKindSelector}
                />
              </Box>

              {dftChartKindSelector.value === DftChartKind.indexPoints ? (
                <ChartDftVersusTenYearReturn
                  sx={{ mt: 4 }}
                  dftItem={data.dftItem}
                  pendoPrefix="asset_class_analysis_dft_versus_ten_year_return"
                  activeElement={activeElement}
                  isWhiteContext={false}
                />
              ) : (
                <ChartDftVersusTenYearReturnBoxAndWhisker
                  sx={{ mt: 4 }}
                  dftItem={data.dftItem}
                  isWhiteContext={false}
                />
              )}
            </>
          )}
        </TablePlaceHolder>
      </TablePlaceHolder>
    );
  },
);
